import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const MoreShakespeare = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', label: 'All Resources', icon: 'Grid' },
    { id: 'academic', label: 'Academic Databases', icon: 'BookOpen' },
    { id: 'performance', label: 'Performance Archives', icon: 'Video' },
    { id: 'analysis', label: 'Literary Analysis', icon: 'FileText' },
    { id: 'interactive', label: 'Interactive Tools', icon: 'Zap' }
  ];

  const featuredResources = [
    {
      name: 'Folger Shakespeare Library',
      url: 'https://www.folger.edu',
      category: 'academic',
      description: 'The world\'s largest Shakespeare collection, offering extensive digital resources, educational materials, and scholarly articles. Features complete texts, lesson plans, and historical context.',
      level: 'All Levels',
      icon: 'Library',
      features: ['Complete Texts', 'Lesson Plans', 'Historical Context', 'Digital Collection']
    },
    {
      name: 'MIT Complete Works of Shakespeare',
      url: 'http://shakespeare.mit.edu',
      category: 'academic',
      description: 'Free online access to the complete works of William Shakespeare. Clean, searchable texts of all plays, sonnets, and poems with no registration required.',
      level: 'All Levels',
      icon: 'Book',
      features: ['Complete Texts', 'Search Function', 'Free Access', 'Mobile Friendly']
    },
    {
      name: 'BBC Shakespeare',
      url: 'https://www.bbc.co.uk/bitesize/topics/shakespeare',
      category: 'interactive',
      description: 'Interactive learning resources including video clips, animations, and study guides. Perfect for visual learners and classroom use with engaging multimedia content.',
      level: 'Beginner to Intermediate',
      icon: 'Tv',
      features: ['Video Content', 'Interactive Quizzes', 'Study Guides', 'Animations']
    }
  ];

  const resourcesByCategory = {
    academic: [
      {
        name: 'Shakespeare Online',
        url: 'http://www.shakespeare-online.com',
        description: 'Comprehensive educational resource with plot summaries, character analyses, and historical background for all of Shakespeare\'s works.',
        level: 'Intermediate',
        tags: ['Study Guides', 'Character Analysis', 'Historical Context']
      },
      {
        name: 'No Sweat Shakespeare',
        url: 'https://www.nosweatshakespeare.com',
        description: 'Modern translations and explanations of Shakespeare\'s plays, making complex language accessible to contemporary readers.',
        level: 'Beginner',
        tags: ['Modern Translation', 'Play Summaries', 'Character Guides']
      },
      {
        name: 'Shakespeare Resource Center',
        url: 'http://www.bardweb.net',
        description: 'Extensive collection of study materials, essays, and research resources for students and scholars.',
        level: 'Advanced',
        tags: ['Research Papers', 'Critical Essays', 'Bibliography']
      },
      {
        name: 'Open Source Shakespeare',
        url: 'https://www.opensourceshakespeare.org',
        description: 'Free, searchable database of Shakespeare\'s works with advanced search capabilities and concordance tools.',
        level: 'All Levels',
        tags: ['Searchable Database', 'Concordance', 'Word Frequency']
      }
    ],
    performance: [
      {
        name: 'Digital Theatre+',
        url: 'https://www.digitaltheatreplus.com',
        description: 'Professional recordings of theatrical productions including many Shakespeare plays. Subscription service with educational pricing.',
        level: 'All Levels',
        tags: ['Video Recordings', 'Professional Productions', 'Educational']
      },
      {
        name: 'Shakespeare\'s Globe',
        url: 'https://www.shakespearesglobe.com',
        description: 'Official site of Shakespeare\'s Globe Theatre with performance archives, virtual tours, and educational resources.',
        level: 'All Levels',
        tags: ['Theatre History', 'Virtual Tours', 'Performance Videos']
      },
      {
        name: 'Royal Shakespeare Company',
        url: 'https://www.rsc.org.uk',
        description: 'Leading Shakespeare theatre company offering production archives, educational resources, and performance insights.',
        level: 'Intermediate to Advanced',
        tags: ['Professional Theatre', 'Behind the Scenes', 'Actor Interviews']
      },
      {
        name: 'YouTube Shakespeare Performances',
        url: 'https://www.youtube.com/results?search_query=shakespeare+full+play',
        description: 'Free collection of full-length Shakespeare play recordings from various productions and time periods.',
        level: 'All Levels',
        tags: ['Free Access', 'Multiple Productions', 'Historical Recordings']
      }
    ],
    analysis: [
      {
        name: 'SparkNotes Shakespeare',
        url: 'https://www.sparknotes.com/shakespeare',
        description: 'Popular study guides with modern translations, chapter summaries, and character analyses for all major works.',
        level: 'Beginner to Intermediate',
        tags: ['Study Guides', 'Modern Translation', 'Chapter Summaries']
      },
      {
        name: 'CliffsNotes Shakespeare',
        url: 'https://www.cliffsnotes.com/literature/shakespeare',
        description: 'Comprehensive study guides with plot summaries, character analysis, and critical essays.',
        level: 'Intermediate',
        tags: ['Plot Analysis', 'Character Studies', 'Critical Commentary']
      },
      {
        name: 'LitCharts Shakespeare',
        url: 'https://www.litcharts.com/shakespeare',
        description: 'Modern study guides featuring side-by-side modern translations, detailed analysis, and visual learning tools.',
        level: 'All Levels',
        tags: ['Modern Translation', 'Visual Guides', 'Detailed Analysis']
      },
      {
        name: 'Shmoop Shakespeare',
        url: 'https://www.shmoop.com/shakespeare',
        description: 'Student-friendly study guides with contemporary language, humor, and multimedia learning resources.',
        level: 'Beginner',
        tags: ['Student Friendly', 'Contemporary Language', 'Multimedia']
      }
    ],
    interactive: [
      {
        name: 'Shakespeare Birthplace Trust',
        url: 'https://www.shakespeare.org.uk',
        description: 'Interactive exhibits, virtual tours of Shakespeare\'s homes, and educational games about Elizabethan England.',
        level: 'All Levels',
        tags: ['Virtual Tours', 'Interactive Games', 'Historical Context']
      },
      {
        name: 'myShakespeare',
        url: 'https://myshakespeare.com',
        description: 'Interactive platform with video lessons, animated summaries, and study tools designed for modern learners.',
        level: 'Beginner to Intermediate',
        tags: ['Video Lessons', 'Animations', 'Interactive Tools']
      },
      {
        name: 'Shakespeare Pro',
        url: 'https://www.shakespearepro.com',
        description: 'Mobile app (also web-based) with complete works, audio recordings, and interactive study features.',
        level: 'All Levels',
        tags: ['Mobile App', 'Audio Books', 'Study Tools']
      },
      {
        name: 'Kahoot! Shakespeare Quizzes',
        url: 'https://kahoot.com/explore/shakespeare',
        description: 'Free interactive quizzes and learning games about Shakespeare\'s works, perfect for classroom or self-study.',
        level: 'Beginner to Intermediate',
        tags: ['Interactive Quizzes', 'Gamified Learning', 'Classroom Ready']
      }
    ]
  };

  const getAllResources = () => {
    const allResources = [];
    Object.keys(resourcesByCategory)?.forEach(category => {
      resourcesByCategory?.[category]?.forEach(resource => {
        allResources?.push({ ...resource, category });
      });
    });
    return allResources;
  };

  const getFilteredResources = () => {
    const resources = selectedCategory === 'all' 
      ? getAllResources() 
      : resourcesByCategory?.[selectedCategory] || [];

    if (searchQuery === '') return resources;

    return resources?.filter(resource =>
      resource?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      resource?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      resource?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
    );
  };

  const filteredResources = getFilteredResources();

  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Helmet>
        <title>More Shakespeare - External Resources | BardPlay</title>
        <meta name="description" content="Curated collection of external Shakespeare learning resources, study guides, and educational materials for students and actors" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/20 to-secondary/20 text-white py-16 border-b border-white/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">More Shakespeare Resources</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                Curated collection of external resources to deepen your understanding and appreciation of Shakespeare's works
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Icon name="Search" className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60" size={20} />
                <input
                  type="text"
                  placeholder="Search resources by name, description, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm text-white placeholder-white/60 shadow-lg focus:ring-2 focus:ring-primary focus:outline-none border border-white/20"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="bg-surface-dark/80 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10 shadow-sm">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-4">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => setSelectedCategory(category?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                    selectedCategory === category?.id
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
                >
                  <Icon name={category?.icon} size={18} />
                  <span className="font-medium">{category?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-12">
          {/* Featured Resources */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <Icon name="Star" size={32} className="text-yellow-500" />
              Featured Resources
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredResources?.map((resource, index) => (
                <div 
                  key={index} 
                  className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/10"
                >
                  <div className="bg-gradient-to-br from-primary/20 to-secondary/20 p-6 text-white border-b border-white/10">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-4">
                      <Icon name={resource?.icon} size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{resource?.name}</h3>
                    <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur rounded-full text-sm">
                      {resource?.level}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-white/80 mb-4 leading-relaxed">{resource?.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {resource?.features?.map((feature, idx) => (
                        <span key={idx} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium border border-primary/30">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => openExternalLink(resource?.url)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors"
                    >
                      Visit Resource
                      <Icon name="ExternalLink" size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Resource Listings */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8">
              {selectedCategory === 'all' ? 'All Resources' : categories?.find(c => c?.id === selectedCategory)?.label}
            </h2>
            
            {filteredResources?.length === 0 ? (
              <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-md p-12 text-center border border-white/10">
                <Icon name="Search" size={64} className="mx-auto text-white/30 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Resources Found</h3>
                <p className="text-white/60">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredResources?.map((resource, index) => (
                  <div 
                    key={index}
                    className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-md p-6 hover:shadow-lg transition-all border border-white/10 group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                          {resource?.name}
                        </h3>
                        <span className="inline-block px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium border border-primary/30">
                          {resource?.level}
                        </span>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                        <Icon name="ExternalLink" size={20} className="text-primary" />
                      </div>
                    </div>
                    
                    <p className="text-white/70 mb-4 leading-relaxed">{resource?.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {resource?.tags?.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-white/10 text-white/80 rounded text-xs font-medium border border-white/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => openExternalLink(resource?.url)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors"
                    >
                      Visit Resource
                      <Icon name="ArrowRight" size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Disclaimer */}
          <section className="bg-yellow-500/10 border-l-4 border-yellow-500 p-8 rounded-lg backdrop-blur-sm">
            <div className="flex gap-4">
              <Icon name="AlertTriangle" size={24} className="text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-white mb-2">External Resources Disclaimer</h3>
                <p className="text-white/80 leading-relaxed">
                  The external websites and resources listed on this page are not associated with or monitored by BardPlay. 
                  These links are provided for reference and educational purposes only. BardPlay does not endorse, control, 
                  or assume responsibility for the content, privacy policies, or practices of any third-party sites. 
                  Users should exercise their own judgment and caution when visiting external websites. Please review each 
                  site's terms of use and privacy policies before sharing any personal information or engaging with their content.
                </p>
              </div>
            </div>
          </section>

          {/* Return to BardPlay CTA */}
          <section className="mt-12 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl shadow-xl p-12 text-center text-white backdrop-blur-sm border border-white/10">
            <h2 className="text-3xl font-bold mb-4">Ready to Continue Your Shakespeare Journey?</h2>
            <p className="text-xl mb-8 text-white/90">
              Return to BardPlay for interactive lessons, quizzes, and personalized learning
            </p>
            <a
              href="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold transition-colors"
            >
              <Icon name="Home" size={20} />
              Back to BardPlay
            </a>
          </section>
        </div>
      </div>
    </>
  );
};

export default MoreShakespeare;